import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DebugSection } from '../DebugSection';
import { useDebugLayer } from '../DebugLayerProvider';
import type { DebugActionDefinition } from '../types';

/**
 * Admin-only Actions — route-gefiltert.
 *
 * Die Actions selbst stammen aus der Registry; sie wrappen einen
 * bereits abgesicherten Backend-Pfad (Mutation, RPC, Edge-Function).
 * Diese Section führt KEINE eigene Logik aus.
 */
export function ActionsSection() {
  const { routeActions } = useDebugLayer();
  if (routeActions.length === 0) return null;
  return (
    <DebugSection
      title="Admin Aktionen"
      description="Wirken auf bereits abgesicherten Backend-Pfaden. Kein Pendant im normalen User-UI."
      visible
    >
      {routeActions.map((a) => (
        <ActionRow key={a.id} action={a} />
      ))}
    </DebugSection>
  );
}

function ActionRow({ action }: { action: DebugActionDefinition }) {
  const [pending, setPending] = useState(false);
  const variant = action.variant ?? 'default';
  const buttonClass =
    variant === 'danger'
      ? 'h-7 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10'
      : variant === 'warning'
        ? 'h-7 px-2 text-xs border-amber-500/40 text-amber-600 hover:bg-amber-500/10'
        : 'h-7 px-2 text-xs';
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug">{action.label}</p>
        {action.description ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            {action.description}
          </p>
        ) : null}
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mt-1">
          Scope: {action.scope}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={buttonClass}
        disabled={pending || action.enabled === false}
        data-testid={`debug-action-${action.id}`}
        onClick={async () => {
          try {
            setPending(true);
            await action.execute();
          } finally {
            setPending(false);
          }
        }}
      >
        {pending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          'Ausführen'
        )}
      </Button>
    </div>
  );
}